// Monetisation → Paywall settings (contracts/paywall.md §2, §4, §5): the paywall.* config keys. When the paywall
// shows as the app opens (paywall.onOpen), the default paywall players outside an experiment see
// (paywall.defaultVariant, with a phone sketch), the products experiments may test and the USD list prices the
// estimates use, the one-time exit offer, and the promotional offers for lapsed and non-renewing subscribers.
// Writes are admin (audited); every role reads.

import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import type { Locale } from "../../api/messaging";
import { useConfig, usePatchConfig } from "../../api/queries";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Select, TextInput } from "../../components/ui";
import { tableStyles } from "../../components/table";
import { toLocalized } from "../../lib/messaging";
import {
  APP_PAYWALL,
  BASE_PRODUCTS,
  COPY_LIMITS,
  ExitOffer,
  OnOpen,
  PLAN_NAME,
  PLANS,
  paywallConfigProblem,
  PRODUCT_RE,
  planOfProduct,
  PromoOffer,
  PromoOffers,
  readPaywallConfig,
  RETURNING,
  RETURNING_LABEL,
  type Returning,
  variantProblems,
} from "../../lib/paywall";
import { ContentEditor, type ContentState, type FieldSpec, SwitchRow, textState } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import { PaywallPreview, VariantEditor, variantOut, type VariantState, variantState } from "./components";
import p from "./paywall.module.css";

type Saved = ReturnType<typeof readPaywallConfig>;
const OFFER_FIELDS = ["title", "body", "badge"] as const;
type OfferKey = (typeof OFFER_FIELDS)[number];
const OFFER_SPECS: readonly FieldSpec<OfferKey>[] = [
  { key: "title", label: "Title", limit: COPY_LIMITS.title, placeholder: "e.g. Wait: Pro for less" },
  { key: "body", label: "Text", limit: COPY_LIMITS.body, rows: 3, placeholder: "e.g. A one-time price for your first year." },
  { key: "badge", label: "Badge", limit: COPY_LIMITS.badge, placeholder: "e.g. One time" },
];
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function PaywallSettingsPage() {
  const config = useConfig();
  const canEdit = useCan("paywall.edit");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Paywall settings"
        meta={
          canEdit ? (
            <span>
              The paywall.* config keys; the app reads them with GET /v1/paywall. Tests of the paywall run in <Link to="/monetisation/experiments">Experiments</Link>.
            </span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · paywall settings need the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      {config.isPending ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : config.isError ? (
        <ErrorState error={config.error} onRetry={() => void config.refetch()} />
      ) : (
        <Settings saved={readPaywallConfig(config.data.values)} canEdit={canEdit} />
      )}
    </>
  );
}

function Settings({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  return (
    <div className={p.stack}>
      <OnOpenCard key={JSON.stringify(saved.onOpen)} saved={saved.onOpen} canEdit={canEdit} />
      <DefaultVariantCard key={JSON.stringify([saved.defaultVariant, saved.allowedProducts])} saved={saved} canEdit={canEdit} />
      <ProductsCard key={JSON.stringify([saved.allowedProducts, saved.prices])} saved={saved} canEdit={canEdit} />
      <ExitOfferCard key={JSON.stringify(saved.exitOffer)} saved={saved} canEdit={canEdit} />
      <PromoOffersCard key={JSON.stringify(saved.promoOffers)} saved={saved} canEdit={canEdit} />
    </div>
  );
}

/**
 * Save… with a confirmation naming every key it writes, in one PATCH /config: all or nothing, and the API's
 * cross-key rule (products must be in paywall.allowedProducts) sees the keys together.
 */
function SaveBar({ writes, problem, canEdit, testId, children }: { writes: { key: string; value: unknown }[]; problem: string | null; canEdit: boolean; testId: string; children?: ReactNode }) {
  const set = usePatchConfig();
  const [open, setOpen] = useState(false);
  if (!canEdit) return null;
  const save = () => set.mutate(Object.fromEntries(writes.map((w) => [w.key, w.value])) as never, { onSuccess: () => setOpen(false) });
  return (
    <div className={p.saveRow}>
      <Button variant="primary" disabled={!writes.length || problem !== null} onClick={() => setOpen(true)} data-testid={`${testId}-save`}>
        Save…
      </Button>
      {problem ? (
        <span className={m.required} data-testid={`${testId}-problem`}>
          {problem}
        </span>
      ) : writes.length ? (
        <span className={styles.faint}>unsaved changes</span>
      ) : null}
      <ConfirmDialog
        open={open}
        title={`Save ${writes.map((w) => w.key).join(" and ")}?`}
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => {
          set.reset();
          setOpen(false);
        }}
        onConfirm={save}
      >
        <p className={styles.prose}>Players get it with their next paywall config (the app caches it and refreshes on open).</p>
        {children}
      </ConfirmDialog>
    </div>
  );
}

// ---------------------------------------------------------------- when the app opens

function OnOpenCard({ saved, canEdit }: { saved: OnOpen; canEdit: boolean }) {
  const [v, setV] = useState({ ...saved, everyDays: String(saved.everyDays), minHoursBetween: String(saved.minHoursBetween) });
  const value = { firstLaunch: v.firstLaunch, returning: v.returning, everyDays: Number(v.everyDays), minHoursBetween: Number(v.minHoursBetween) };
  const parsed = OnOpen.safeParse(value);
  const problem = parsed.success ? null : value.everyDays < 1 || value.everyDays > 60 || !Number.isInteger(value.everyDays) ? "Every N days is 1 to 60." : "The gap is 0 to 336 hours.";
  return (
    <Card title="When the app opens" actions={<span className="mono">paywall.onOpen</span>}>
      <fieldset className={p.cardBody} disabled={!canEdit} style={{ border: 0, margin: 0, padding: 0 }} data-testid="on-open">
        <SwitchRow
          title="After onboarding"
          hint="Every new player sees the paywall once, right after onboarding and before Home (trigger onboarding). It closes with the X."
          checked={v.firstLaunch}
          onChange={(x) => setV((s) => ({ ...s, firstLaunch: x }))}
          label="After onboarding"
          testId="on-open-first"
        />
        <div className={p.inline}>
          <Field label="Returning free players" hint="On a fresh open: a cold launch, or back after 30 minutes or more. Never from a push, a link or a widget, never in a solve.">
            <Select value={v.returning} onChange={(e) => setV((s) => ({ ...s, returning: e.target.value as Returning }))} data-testid="on-open-returning">
              {RETURNING.map((r) => (
                <option key={r} value={r}>
                  {RETURNING_LABEL[r]}
                </option>
              ))}
            </Select>
          </Field>
          {v.returning === "every_n_days" ? (
            <Field label="Every (days)">
              <TextInput type="number" min={1} max={60} value={v.everyDays} onChange={(e) => setV((s) => ({ ...s, everyDays: e.target.value }))} data-testid="on-open-days" />
            </Field>
          ) : null}
          <Field label="At least (hours) apart" hint="Since the last automatic paywall.">
            <TextInput type="number" min={0} max={336} step={0.5} value={v.minHoursBetween} onChange={(e) => setV((s) => ({ ...s, minHoursBetween: e.target.value }))} data-testid="on-open-gap" />
          </Field>
        </div>
        <SaveBar writes={parsed.success && !same(parsed.data, saved) ? [{ key: "paywall.onOpen", value: parsed.data }] : []} problem={problem} canEdit={canEdit} testId="on-open" />
      </fieldset>
    </Card>
  );
}

// ---------------------------------------------------------------- default variant

function DefaultVariantCard({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  const [state, setState] = useState<VariantState>(() => variantState(saved.defaultVariant));
  const [locale, setLocale] = useState<Locale>("en");
  const value = variantOut(state);
  const problems = variantProblems(value, saved.allowedProducts);
  const baseline = variantOut(variantState(saved.defaultVariant));
  return (
    <Card title="Default paywall" actions={<span className="mono">paywall.defaultVariant</span>}>
      <div className={p.split}>
        <fieldset className={p.cardBody} disabled={!canEdit} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <p className={styles.faint}>What every player outside a running experiment sees; a variant's empty fields fall back to it, and its own empty fields to the app's built-in paywall.</p>
          <VariantEditor
            value={state}
            onChange={setState}
            allowed={saved.allowedProducts}
            prices={saved.prices}
            locale={locale}
            onLocale={setLocale}
            exitOfferReady={saved.exitOffer.enabled}
            fallback={APP_PAYWALL}
            testPrefix="default"
          />
          {problems.length ? (
            <ul className={p.problems} data-testid="default-problems">
              {problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : null}
          <SaveBar writes={!same(value, baseline) ? [{ key: "paywall.defaultVariant", value }] : []} problem={problems[0] ?? null} canEdit={canEdit} testId="default" />
        </fieldset>
        <div>
          <div className={p.previewLabel}>
            <span className="monoLabel">Preview · {locale}</span>
          </div>
          <PaywallPreview variant={value} locale={locale} prices={saved.prices} fallback={{}} />
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- products and prices

function ProductsCard({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  const [allowed, setAllowed] = useState(saved.allowedProducts);
  const [prices, setPrices] = useState(() => Object.fromEntries(Object.entries(saved.prices).map(([k, v]) => [k, String(v)])));
  const [draft, setDraft] = useState("");
  const base = PLANS.map((x) => BASE_PRODUCTS[x]);
  const rows = [...new Set([...base, ...allowed, ...Object.keys(prices)])];
  const priceValue = Object.fromEntries(Object.entries(prices).filter(([, v]) => v.trim() !== "").map(([k, v]) => [k, Number(v)]));
  const badPrice = Object.entries(priceValue).find(([, v]) => !Number.isFinite(v) || v < 0 || v > 10_000);
  const draftProblem = draft.trim() && !PRODUCT_RE.test(draft.trim()) ? "iqdaily.pro.<monthly|annual|lifetime>[.<suffix>], e.g. iqdaily.pro.annual.offer" : null;
  // The API's cross-key rule: a product still used by the default paywall, the exit offer or a promo offer stays allowed.
  const relation = paywallConfigProblem(["paywall.allowedProducts"], (k) =>
    k === "paywall.allowedProducts" ? allowed : k === "paywall.defaultVariant" ? saved.defaultVariant : k === "paywall.exitOffer" ? saved.exitOffer : saved.promoOffers,
  );
  const problem = badPrice
    ? `${badPrice[0]}: a price from 0 to 10 000.`
    : Object.keys(priceValue).length > 30
      ? "At most 30 prices."
      : allowed.length > 30
        ? "At most 30 allowed products."
        : relation
          ? `Still in use: ${relation.message}`
          : null;
  const add = () => {
    const id = draft.trim();
    if (!id || draftProblem || allowed.includes(id) || base.includes(id)) return;
    setAllowed([...allowed, id]);
    setDraft("");
  };
  const writes = [
    ...(!same(allowed, saved.allowedProducts) ? [{ key: "paywall.allowedProducts", value: allowed }] : []),
    ...(!same(priceValue, saved.prices) ? [{ key: "paywall.prices", value: priceValue }] : []),
  ];
  return (
    <Card title="Products and list prices" actions={<span className="mono">paywall.allowedProducts · paywall.prices</span>}>
      <fieldset className={p.cardBody} disabled={!canEdit} style={{ border: 0, margin: 0, padding: 0 }} data-testid="products">
        <p className={styles.faint}>
          The default paywall, experiments, the exit offer and promo offers may use only the products listed here (the base three always stay): same subscription group, live in App
          Store Connect. The USD list prices are for the revenue estimates only; the app always shows StoreKit's price.
        </p>
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Product</th>
                <th>Use</th>
                <th>List price (USD)</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {rows.map((id) => {
                const plan = PLANS.find((x) => BASE_PRODUCTS[x] === id);
                return (
                  <tr key={id} data-testid="product-row">
                    <td className={tableStyles.mono}>{id}</td>
                    <td>{plan ? <Chip tone="muted">{PLAN_NAME[plan]} · base</Chip> : allowed.includes(id) ? <Chip tone="violet">allowed for tests</Chip> : <Chip tone="muted">price only</Chip>}</td>
                    <td>
                      <TextInput type="number" min={0} step={0.01} value={prices[id] ?? ""} placeholder="—" onChange={(e) => setPrices((s) => ({ ...s, [id]: e.target.value }))} aria-label={`${id} price`} style={{ width: 110 }} />
                    </td>
                    <td>
                      {allowed.includes(id) && !base.includes(id) ? (
                        <Button size="sm" variant="ghost" onClick={() => setAllowed(allowed.filter((x) => x !== id))} aria-label={`Remove ${id}`}>
                          Remove
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={p.inline}>
          <Field label="Allow a product" hint={draftProblem ?? "e.g. iqdaily.pro.monthly.b, a cheaper monthly price to test"}>
            <TextInput
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="iqdaily.pro.annual.offer"
              data-testid="allow-product"
            />
          </Field>
          <Button variant="secondary" onClick={add} disabled={!draft.trim() || draftProblem !== null} style={{ marginTop: 22 }}>
            Add
          </Button>
        </div>
        <SaveBar writes={writes} problem={problem} canEdit={canEdit} testId="products" />
      </fieldset>
    </Card>
  );
}

// ---------------------------------------------------------------- exit offer

function ExitOfferCard({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  const [enabled, setEnabled] = useState(saved.exitOffer.enabled);
  const [productId, setProductId] = useState(saved.exitOffer.productId);
  const [maxShows, setMaxShows] = useState(String(saved.exitOffer.maxShows));
  const [text, setText] = useState<ContentState<OfferKey>>(() => textState(OFFER_FIELDS, saved.exitOffer.content));
  const [locale, setLocale] = useState<Locale>("en");
  const value = { enabled, productId, content: toLocalized(text.draft, OFFER_FIELDS).content, maxShows: Number(maxShows) };
  const baseline = { ...saved.exitOffer, content: toLocalized(textState(OFFER_FIELDS, saved.exitOffer.content).draft, OFFER_FIELDS).content };
  const parsed = ExitOffer.safeParse(value);
  const problem =
    !text.draft.en.title.trim() || !text.draft.en.body.trim()
      ? "Write the English title and text."
      : !Number.isInteger(value.maxShows) || value.maxShows < 1 || value.maxShows > 5
        ? "Shown at most 1 to 5 times a player."
        : !saved.allowedProducts.includes(productId)
          ? `${productId} isn't in paywall.allowedProducts.`
          : !parsed.success
            ? "Check the text lengths."
            : null;
  const options = [...new Set([...saved.allowedProducts, productId])];
  const pick = (k: OfferKey) => text.draft[locale][k].trim() || text.draft.en[k].trim();
  return (
    <Card title="Exit offer" actions={<Chip tone={saved.exitOffer.enabled ? "success" : "muted"}>{saved.exitOffer.enabled ? "on" : "off"}</Chip>}>
      <div className={p.split}>
        <fieldset className={p.cardBody} disabled={!canEdit} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }} data-testid="exit-offer">
          <SwitchRow
            title="Offer it"
            hint="When a free player closes an automatic paywall whose variant has the exit offer on, up to the times below per player, ever. Keep it off until the product is approved in App Store Connect with a build."
            checked={enabled}
            onChange={setEnabled}
            label="Exit offer on"
            testId="exit-enabled"
          />
          <Field label="Product" hint={saved.prices[productId] !== undefined ? `List price $${saved.prices[productId]!.toFixed(2)}` : "No list price in paywall.prices: revenue estimates skip it."}>
            <Select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="exit-product">
              {options.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Shown at most (times a player, ever)">
            <TextInput type="number" min={1} max={5} value={maxShows} onChange={(e) => setMaxShows(e.target.value)} style={{ width: 100 }} data-testid="exit-max-shows" />
          </Field>
          <ContentEditor value={text} onChange={setText} fields={OFFER_SPECS} englishRequired={["title", "body"]} locale={locale} onLocale={setLocale} />
          <SaveBar writes={parsed.success && !same(parsed.data, baseline) ? [{ key: "paywall.exitOffer", value: parsed.data }] : []} problem={problem} canEdit={canEdit} testId="exit" />
        </fieldset>
        <div>
          <div className={p.previewLabel}>
            <span className="monoLabel">Sheet · {locale}</span>
          </div>
          <div className={p.sheet} data-testid="exit-preview">
            {pick("badge") ? <i className={p.badge}>{pick("badge")}</i> : null}
            <strong className={p.headline}>{pick("title") || "Title"}</strong>
            <span className={p.sub}>{pick("body")}</span>
            <span className={p.price}>{saved.prices[productId] !== undefined ? `$${saved.prices[productId]!.toFixed(2)}/year` : productId}</span>
            <span className={p.cta}>Claim the offer</span>
            <span className={p.legal}>Restore · Terms · Privacy</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- promotional offers

const AUDIENCE_LABEL: Record<PromoOffer["audience"], string> = { lapsed: "Lapsed (expired over 7 days ago)", renewal_off: "Renewal off (active, won't renew)" };

function PromoOffersCard({ saved, canEdit }: { saved: Saved; canEdit: boolean }) {
  const [offers, setOffers] = useState(saved.promoOffers);
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  return (
    <Card
      title={`Promotional offers · ${offers.length}`}
      actions={
        canEdit ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing({ index: null })} data-testid="promo-add">
            Add offer
          </Button>
        ) : null
      }
    >
      <div className={p.cardBody} data-testid="promo-offers">
        <p className={styles.faint}>
          For subscribers, with an offer ID set in App Store Connect; the server signs each purchase with the In-App Purchase key. The paywall shows the first offer that matches the player.
        </p>
        {offers.length === 0 ? (
          <EmptyState title="No promotional offers">Add one once its offer ID exists in App Store Connect.</EmptyState>
        ) : (
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Offer</th>
                  <th>Audience</th>
                  <th>Languages</th>
                  {canEdit ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {offers.map((o, i) => (
                  <tr key={o.id} data-testid="promo-row">
                    <td className={tableStyles.mono}>{i + 1}</td>
                    <td>
                      <strong>{o.content.en?.title ?? o.id}</strong>
                      <span className={p.when}>
                        <span className="mono">{o.id}</span> · {o.productId} · offer {o.offerId}
                      </span>
                    </td>
                    <td>{AUDIENCE_LABEL[o.audience]}</td>
                    <td className={tableStyles.mono}>{Object.keys(o.content).length}</td>
                    {canEdit ? (
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ index: i })}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => setOffers(offers.map((x, j) => (j === i - 1 ? offers[i]! : j === i ? offers[i - 1]! : x)))} aria-label={`Move ${o.id} up`}>
                          ↑
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setOffers(offers.filter((_, j) => j !== i))} aria-label={`Remove ${o.id}`}>
                          Remove
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <SaveBar
          writes={!same(offers, saved.promoOffers) ? [{ key: "paywall.promoOffers", value: offers }] : []}
          problem={(() => {
            const r = PromoOffers.safeParse(offers);
            return r.success ? null : (r.error.issues[0]?.message ?? "Check the offers.");
          })()}
          canEdit={canEdit}
          testId="promo"
        />
      </div>
      {editing ? (
        <PromoDialog
          offer={editing.index === null ? null : (offers[editing.index] ?? null)}
          taken={offers.filter((_, j) => j !== editing.index).map((o) => o.id)}
          products={saved.allowedProducts.filter((x) => planOfProduct(x) === "monthly" || planOfProduct(x) === "annual")}
          onClose={() => setEditing(null)}
          onSave={(o) => {
            setOffers(editing.index === null ? [...offers, o] : offers.map((x, j) => (j === editing.index ? o : x)));
            setEditing(null);
          }}
        />
      ) : null}
    </Card>
  );
}

function PromoDialog({ offer, taken, products, onClose, onSave }: { offer: PromoOffer | null; taken: string[]; products: string[]; onClose: () => void; onSave: (o: PromoOffer) => void }) {
  const [id, setId] = useState(offer?.id ?? "");
  const [productId, setProductId] = useState(offer?.productId ?? BASE_PRODUCTS.annual);
  const [offerId, setOfferId] = useState(offer?.offerId ?? "");
  const [audience, setAudience] = useState<PromoOffer["audience"]>(offer?.audience ?? "lapsed");
  const [text, setText] = useState<ContentState<OfferKey>>(() => textState(OFFER_FIELDS, offer?.content));
  const [locale, setLocale] = useState<Locale>("en");
  const value = { id: id.trim(), productId, offerId: offerId.trim(), audience, content: toLocalized(text.draft, OFFER_FIELDS).content };
  const parsed = PromoOffer.safeParse(value);
  const problem = !/^[a-z0-9][a-z0-9_-]{0,39}$/.test(value.id)
    ? "The id is lowercase letters, digits, - and _, starting with a letter or digit (e.g. winback_50)."
    : taken.includes(value.id)
      ? "Another offer has this id."
      : !/^[A-Za-z0-9._-]{1,64}$/.test(value.offerId)
        ? "The offer ID from App Store Connect (letters, digits, . _ -)."
        : !text.draft.en.title.trim() || !text.draft.en.body.trim()
          ? "Write the English title and text."
          : !parsed.success
            ? "Check the text lengths."
            : null;
  return (
    <Dialog
      open
      width={640}
      title={offer ? `Edit ${offer.id}` : "Add a promotional offer"}
      onClose={onClose}
      footer={
        <>
          <span className={m.required}>{problem}</span>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={problem !== null} onClick={() => parsed.success && onSave(parsed.data)} data-testid="promo-done">
            {offer ? "Done" : "Add"}
          </Button>
        </>
      }
    >
      <div className={p.cardBody}>
        <div className={p.inline}>
          <Field label="Id">
            <TextInput value={id} onChange={(e) => setId(e.target.value)} placeholder="winback_50" data-testid="promo-id" />
          </Field>
          <Field label="Offer ID (App Store Connect)">
            <TextInput value={offerId} onChange={(e) => setOfferId(e.target.value)} placeholder="winback_50_3m" data-testid="promo-offer-id" />
          </Field>
        </div>
        <div className={p.inline}>
          <Field label="Subscription">
            <Select value={productId} onChange={(e) => setProductId(e.target.value)}>
              {[...new Set([...products, productId])].map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Who">
            <Segmented
              label="Audience"
              value={audience}
              onChange={setAudience}
              options={[
                { value: "lapsed", label: "Lapsed" },
                { value: "renewal_off", label: "Renewal off" },
              ]}
            />
          </Field>
        </div>
        <ContentEditor value={text} onChange={setText} fields={OFFER_SPECS} englishRequired={["title", "body"]} locale={locale} onLocale={setLocale} />
      </div>
    </Dialog>
  );
}
